import React, { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';

import { Icon } from '@column/column-ui-kit';
import {
  AnimatedCheckIcon,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CountryFlag,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@column/ui';

import { useDebounce, useInstitutions } from '~/hooks';
import { COUNTRIES, countryName } from '~/lib/transfers/intlWire';
import { FinancialInstitution } from '~/repositories';

import { InstitutionFlag, institutionName } from './institution';

interface InstitutionSearchProps {
  /** The selected institution; the input shows its name, including when it's found by BIC or routing number. */
  value: FinancialInstitution | null;
  onChange: (institution: FinancialInstitution) => void;
}

const CountryFilter: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // The selected country and the US sit above the full list, which still includes them.
  const pinned = COUNTRIES.filter((entry) => entry.countryCode === 'US' || entry.countryCode === value).sort(
    (a, b) => Number(b.countryCode === value) - Number(a.countryCode === value)
  );

  const renderItem = (entry: (typeof COUNTRIES)[number], isPinned = false) => (
    <CommandItem
      key={entry.countryCode}
      // cmdk tracks items by value, so pinned copies need their own to highlight separately.
      value={`${entry.countryName} (${entry.countryCode})${isPinned ? ' pinned' : ''}`}
      onSelect={() => {
        onChange(entry.countryCode);
        setOpen(false);
      }}
      className="pr-8"
    >
      <CountryFlag country={entry.countryCode} size={16} />
      <span className="truncate">
        {entry.countryName} ({entry.countryCode})
      </span>
      <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
        <AnimatedCheckIcon checked={entry.countryCode === value} />
      </span>
    </CommandItem>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="muted"
          size="sm"
          className={cn('gap-1.5 rounded-md px-2.5 shadow-none', !value && 'text-gray-400 dark:text-gray-500')}
        >
          {value && <CountryFlag country={value} size={14} />}
          {value || 'Country'}
          {open ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-56 p-0">
        <Command>
          <CommandInput placeholder="Search country…" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>No results found</CommandEmpty>
            {/* Hidden while searching so matches aren't listed twice. */}
            {!search && (
              <>
                <CommandGroup>{pinned.map((entry) => renderItem(entry, true))}</CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup>{COUNTRIES.map((entry) => renderItem(entry))}</CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const InstitutionSearch: FC<InstitutionSearchProps> = ({ value, onChange }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchCountryCode, setSearchCountryCode] = useState('');
  const [institutions, setInstitutions] = useState<FinancialInstitution[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const { createRequest: fetchInstitutions } = useInstitutions(false)({
    onSuccess: (data) => setInstitutions(data?.institutions ?? []),
  });

  useEffect(() => {
    setSearchValue(value ? institutionName(value) : '');
  }, [value?.routingNumber]);

  const [fetchInstitutionsDebounce] = useDebounce((name: string) => {
    fetchInstitutions(searchCountryCode.length > 1 ? { name, countryCode: searchCountryCode } : { name });
  }, 100);

  useEffect(() => {
    setActiveIndex(-1);

    if (searchValue.length === 0) {
      fetchInstitutions();
      return;
    }

    fetchInstitutionsDebounce(searchValue);
  }, [searchValue]);

  const handleCountryChange = (countryCode: string) => {
    // Picking the active country again clears the filter.
    const next = countryCode === searchCountryCode ? '' : countryCode;
    const name = searchValue.length > 0 ? { name: searchValue } : {};

    setSearchCountryCode(next);
    fetchInstitutions(next ? { ...name, countryCode: next } : name);
  };

  const handleSelect = (entry: FinancialInstitution) => {
    onChange(entry);
    setSearchValue(institutionName(entry));
    setIsOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }

    if (event.key === 'ArrowDown' && !isOpen) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isOpen || institutions.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % institutions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? institutions.length - 1 : index - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      handleSelect(institutions[activeIndex]);
    }
  };

  const searchCountryName = countryName(searchCountryCode);

  return (
    <Popover open={isOpen && institutions.length > 0} onOpenChange={setIsOpen}>
      <PopoverAnchor asChild>
        <div ref={anchorRef}>
          <InputGroup className="h-10">
            <InputGroupAddon>
              <Icon.Search />
            </InputGroupAddon>
            <InputGroupInput
              value={searchValue}
              onChange={(event) => {
                setSearchValue(event.target.value);
                setIsOpen(true);
              }}
              // Not on focus: the dialog focuses this field when it opens, and the list would cover the form.
              onClick={() => setIsOpen(true)}
              onBlur={() => setIsOpen(false)}
              onKeyDown={handleKeyDown}
              placeholder={
                searchCountryName ? `Search institution name in ${searchCountryName}` : 'Search institution name'
              }
              autoComplete="off"
            />
            <InputGroupAddon align="inline-end">
              <CountryFilter value={searchCountryCode} onChange={handleCountryChange} />
            </InputGroupAddon>
          </InputGroup>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={6}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
        className="max-h-72 w-(--radix-popover-trigger-width) overflow-y-auto p-1"
      >
        <ul role="listbox" onMouseDown={(event) => event.preventDefault()}>
          {institutions.map((entry, index) => (
            <li
              key={`${entry.routingNumber}-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => handleSelect(entry)}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                index === activeIndex ? 'bg-accent text-accent-foreground' : 'text-gray-600 dark:text-gray-100'
              )}
            >
              <InstitutionFlag countryCode={entry.countryCode} size={20} />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate font-medium">{institutionName(entry)}</span>
                <span className="truncate text-xs text-muted-foreground">
                  <span className="font-mono">{entry.routingNumber}</span> · {countryName(entry.countryCode)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
